/* eslint-disable restrict-imports/restrict-imports */
import { useState } from 'react';
import { Button, makeStyles, CircularProgress, TextInput, Box, Paper } from 'design-system';
import { Autocomplete } from '@material-ui/lab';
import Draggable from 'react-draggable';

interface FindAndReplaceProps {
  isOpen: boolean;
  onClose: () => void;
  onCancel: (find: string, column: string) => void;
  onFindAndReplace: (find: string, replace: string, column: string) => void;
  editableColumns: string[];
  loading: boolean;
}

const FindAndReplace: React.FC<FindAndReplaceProps> = ({
  isOpen,
  onClose,
  onCancel,
  onFindAndReplace,
  editableColumns,
  loading,
}) => {
  const classes = useStyles();
  const [column, setColumn] = useState<string>('');
  const [findValue, setFindValue] = useState<string>('');
  const [replaceValue, setReplaceValue] = useState<string>('');
  const [lastSearchedColumn, setLastSearchedColumn] = useState<string>('');
  const [lastSearchedFind, setLastSearchedFind] = useState<string>('');

  const onFindAndReplaceWrapper = () => {
    if (lastSearchedFind || lastSearchedColumn) {
      onCancel(lastSearchedFind, lastSearchedColumn);
    }
    onFindAndReplace(findValue, replaceValue, column);
    if (!replaceValue) {
      setLastSearchedColumn(column);
      setLastSearchedFind(findValue);
    }
  };

  const onCloseWrapper = () => {
    onCancel(lastSearchedFind, lastSearchedColumn);
    onClose();
    setColumn('');
    setFindValue('');
    setReplaceValue('');
    setLastSearchedColumn('');
    setLastSearchedFind('');
  };

  return (
    <div id="hello!" style={{ position: 'absolute' }}>
      {isOpen && (
        <Draggable cancel="input">
          <Paper className={classes.popper}>
            <Box id="hello there" className={classes.content}>
              <div className={classes.row}>
                Find{' '}
                <TextInput
                  value={findValue}
                  onChange={e => {
                    setFindValue(e.target.value);
                  }}
                />
              </div>
              <div className={classes.row}>
                Replace with{' '}
                <TextInput
                  value={replaceValue}
                  onChange={e => {
                    setReplaceValue(e.target.value);
                  }}
                />
              </div>
              <div className={classes.row}>
                Search{' '}
                <Autocomplete
                  size="small"
                  fullWidth
                  options={editableColumns}
                  onChange={(_e, newValue: string | null) => setColumn(newValue ?? '')}
                  renderInput={(params: any) => <TextInput {...params} fullWidth size="small" />}
                />
              </div>
              <div className={classes.row}>
                <Button variant="outlined" type="secondary" onClick={onCloseWrapper}>
                  Cancel
                </Button>
                <Button
                  type="primary"
                  onClick={onFindAndReplaceWrapper}
                  disabled={!findValue || (!!replaceValue && !column)}
                >
                  {replaceValue ? 'Find And Replace' : 'Find'}
                  {loading && (
                    <CircularProgress
                      size={12}
                      color="secondary"
                      thickness={6}
                      className={classes.circularProgress}
                    />
                  )}
                </Button>
              </div>
            </Box>
          </Paper>
        </Draggable>
      )}
    </div>
  );
};

const useStyles = makeStyles({
  popper: {
    zIndex: 1100,
    backgroundColor: 'white',
    border: '1px solid #CCCCCC',
    padding: '8px',
    borderRadius: 8,
    position: 'relative',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  circularProgress: {
    marginLeft: '8px',
  },
});

export default FindAndReplace;
